import React from 'react';
import {
  buildGuideItemRows,
  buildGuideReferenceRows,
  docTypeLabel,
  formatGuideDate,
  formatWarehouseRef,
  getGuideCurrencySymbol,
  getGuideStatusInfo,
  getGuideTableColumns,
  getGuideTitle,
  getGuideTotalDisplay,
  shouldHideProjectColumn,
  shouldShowGuideTotal,
} from '../../utils/entryGuideDocumentHelpers';

const EntryGuidePrintDocument = ({ guide }) => {
  if (!guide) return null;

  const status = getGuideStatusInfo(guide);
  const isAjuste = guide.guide_type === 'ajuste';
  const isTraspaso = guide.guide_type === 'traspaso';
  const currencySymbol = getGuideCurrencySymbol(guide);
  const references = buildGuideReferenceRows(guide);
  const columns = getGuideTableColumns(guide);
  const items = buildGuideItemRows(guide);
  const hideProject = shouldHideProjectColumn(guide);
  const colClass = hideProject ? 'cols-5' : 'cols-6';

  return (
    <div className="print-container">
      <h1 className="doc-title">{getGuideTitle(guide)}</h1>
      <hr className="doc-title-rule" />

      <div className="info-line">
        <span className="info-label">Guía:</span> {guide.code || '—'}
        <span className="info-sep">·</span>
        <span className="info-label">Fecha:</span> {formatGuideDate(guide.issue_date) || '—'}
        <span className="info-sep">·</span>
        <span className="info-label">Estado:</span>{' '}
        <span className={`status-badge status-${status.tone}`}>{status.label}</span>
      </div>

      <div className="info-line">
        <span className="info-label">{isTraspaso ? 'Almacén origen:' : 'Almacén:'}</span>{' '}
        {formatWarehouseRef(guide.warehouse) || '—'}
        {isTraspaso && guide.destination_warehouse && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">Destino:</span>{' '}
            {formatWarehouseRef(guide.destination_warehouse)}
          </>
        )}
      </div>

      <div className="info-line">
        <span className="info-label">Entidad:</span> {guide.supplier?.name || (isAjuste ? '—' : 'N/A')}
        {guide.supplier?.document_number && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">{docTypeLabel(guide.supplier)}:</span>{' '}
            {guide.supplier.document_number}
          </>
        )}
        {!isAjuste && guide.currency && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">Moneda:</span> {guide.currency === 'USD' ? 'USD' : 'PEN'}
          </>
        )}
      </div>

      {guide.entry_date && (
        <div className="info-line">
          <span className="info-label">Fecha de ingreso:</span> {formatGuideDate(guide.entry_date)}
          {guide.entry_time && (
            <>
              <span className="info-sep">·</span>
              <span className="info-label">Hora:</span> {guide.entry_time}
            </>
          )}
        </div>
      )}

      {references.length > 0 && (
        <div className="refs-block">
          <div className="refs-title">Referencias</div>
          {references.map((ref) => (
            <div key={`${ref.type}-${ref.code}`} className="ref-line">
              <span className="info-label">{ref.type}:</span> {ref.code}
              {ref.detail && ref.detail !== '—' && (
                <>
                  <span className="info-sep">·</span>
                  {ref.detail}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="items-section">
        <div className="section-title">Items de la Guía</div>

        <div className="items-table-wrap">
          <table className={`items-table ${colClass}`}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    className={
                      col === 'Cant.' || col === 'P. Unit' || col === 'Subtotal' ? 'num' : col === 'Unidad' ? 'center' : ''
                    }
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>Sin ítems registrados</td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td className="center">{item.unit}</td>
                    {!hideProject && <td>{item.project}</td>}
                    <td className="num">{item.quantity}</td>
                    <td className="num">{item.unitPrice}</td>
                    <td className="subtotal-cell">{item.subtotal}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-footer">
          {guide.notes ? (
            <div className="print-footer-obs">
              <div className="print-obs-label">Observaciones:</div>
              <div className="print-obs-text">{guide.notes}</div>
            </div>
          ) : null}
          {shouldShowGuideTotal(guide) && (
            <div className="print-footer-total">
              <span className="print-total-label">Total:</span>
              <span className="print-total-value">{getGuideTotalDisplay(guide)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntryGuidePrintDocument;
